/**
 * What a company states about itself (Settings › Company profile), for the
 * documents it sends: its legal name, address, contact and tax identifiers.
 *
 * Any member may read it (`workspace_settings_read`, 0003): a statement a
 * collector prints carries the company's letterhead, not theirs. Nothing here
 * is a payment detail — the profile holds none.
 */
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

export interface CompanyProfile {
    legalName: string;
    taxId: string;
    taxIdLabel: string;
    secondaryTaxId: string;
    secondaryTaxIdLabel: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    contactEmail: string;
    contactPhone: string;
    /** "Shown on statements and reminder emails, if a company wants one" (0012). */
    footerNote: string;
}

const EMPTY: CompanyProfile = {
    legalName: '', taxId: '', taxIdLabel: 'Tax ID', secondaryTaxId: '', secondaryTaxIdLabel: '',
    address: '', city: '', state: '', postalCode: '', contactEmail: '', contactPhone: '', footerNote: '',
};

/** The profile, or the empty one: a document without an address is still a document. */
export function useCompanyProfile(workspaceId: string): { loading: boolean; profile: CompanyProfile } {
    const [state, setState] = useState<{ loading: boolean; profile: CompanyProfile }>({ loading: true, profile: EMPTY });
    const issuedFor = useRef(workspaceId);
    useEffect(() => {
        issuedFor.current = workspaceId;
        void supabase().from('workspace_settings')
            .select('legal_name, tax_id, tax_id_label, secondary_tax_id, secondary_tax_id_label, address, city, state, postal_code, contact_email, contact_phone, footer_note')
            .eq('workspace_id', workspaceId).maybeSingle()
            .then(({ data }) => {
                if (issuedFor.current !== workspaceId) return;
                const r = (data ?? {}) as Record<string, string | null | undefined>;
                const text = (key: string, fallback = '') => (typeof r[key] === 'string' ? r[key]!.trim() : '') || fallback;
                setState({
                    loading: false,
                    profile: {
                        legalName: text('legal_name'), taxId: text('tax_id'), taxIdLabel: text('tax_id_label', EMPTY.taxIdLabel),
                        secondaryTaxId: text('secondary_tax_id'), secondaryTaxIdLabel: text('secondary_tax_id_label'),
                        address: text('address'), city: text('city'), state: text('state'), postalCode: text('postal_code'),
                        contactEmail: text('contact_email'), contactPhone: text('contact_phone'), footerNote: text('footer_note'),
                    },
                });
            }, () => { if (issuedFor.current === workspaceId) setState({ loading: false, profile: EMPTY }); });
    }, [workspaceId]);
    return state;
}
